// Client-side plumbing for the streaming `POST …/github/connect` response.
// The connect endpoint answers with `text/event-stream` (see server
// github/routes.ts): git sideband `progress` events, then a `summary`, then
// `done` or `error`. Native EventSource can't POST, so we read the fetch body
// with a reader and parse SSE frames here. Pure + framework-free so it unit
// tests without a DOM.

export interface SseEvent {
  readonly event: string;
  readonly data: string;
}

/**
 * Parse an SSE byte stream, invoking `onEvent` per complete `event:/data:`
 * frame (frames are `\n\n`-separated). Resolves when the stream ends.
 */
export async function parseSseStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (ev: SseEvent) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (value) buffer += decoder.decode(value, { stream: true });
    if (done) buffer += decoder.decode();
    let sep: number;
    // A frame is terminated by a blank line (\n\n); tolerate \r\n too.
    while ((sep = indexOfFrameEnd(buffer)) !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep).replace(/^(\r?\n){2}/, "");
      const ev = parseFrame(frame);
      if (ev) onEvent(ev);
    }
    if (done) {
      // A stream may end without a final blank line; dispatch a leftover frame.
      const ev = buffer.trim() ? parseFrame(buffer) : null;
      if (ev) onEvent(ev);
      break;
    }
  }
}

function indexOfFrameEnd(buffer: string): number {
  const lf = buffer.indexOf("\n\n");
  const crlf = buffer.indexOf("\r\n\r\n");
  if (lf === -1) return crlf;
  if (crlf === -1) return lf;
  return Math.min(lf, crlf);
}

function parseFrame(frame: string): SseEvent | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of frame.split(/\r?\n/)) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""));
  }
  if (dataLines.length === 0 && event === "message") return null;
  return { event, data: dataLines.join("\n") };
}

/**
 * Pull a percentage out of a git sideband progress line
 * (`Receiving objects:  42% (n/m)`, `Resolving deltas: 100%`). Null when the
 * line carries no percentage (e.g. `remote: Enumerating objects…`).
 */
export function parseSidebandPercent(line: string): number | null {
  const m = /(\d+)%/.exec(line);
  if (!m) return null;
  const pct = Number(m[1]);
  return pct >= 0 && pct <= 100 ? pct : null;
}
